/**
 * PaymentBadges — “Verified by Visa”, Mastercard SecureCode, Amex SafeKey,
 * RuPay, PayPal, UPI logos. Pure CSS/SVG so we don’t hot-link to bank assets.
 * Reuse on the Checkout page and footer to build buyer trust.
 *
 * Sizes:
 *   sm  → row of small badges (footer)
 *   md  → wider badges with subtle border (Checkout)
 */
const VISA = (
  <svg viewBox="0 0 64 22" aria-label="Visa" className="block">
    <text x="2" y="18" fontFamily="Helvetica, Arial, sans-serif" fontWeight="900" fontStyle="italic" fontSize="20" fill="#1A1F71" letterSpacing="-0.5">VISA</text>
  </svg>
);
const MASTERCARD = (
  <svg viewBox="0 0 48 30" aria-label="Mastercard">
    <circle cx="18" cy="15" r="11" fill="#EB001B"/>
    <circle cx="30" cy="15" r="11" fill="#F79E1B"/>
    <path d="M24 7.5a11 11 0 0 1 0 15 11 11 0 0 1 0-15z" fill="#FF5F00"/>
  </svg>
);
const AMEX = (
  <svg viewBox="0 0 64 26" aria-label="American Express">
    <rect x="0" y="0" width="64" height="26" rx="3" fill="#006FCF"/>
    <text x="32" y="17" textAnchor="middle" fontFamily="Helvetica, Arial, sans-serif" fontWeight="900" fontSize="9" fill="#FFFFFF" letterSpacing="0.6">AMERICAN</text>
    <text x="32" y="24" textAnchor="middle" fontFamily="Helvetica, Arial, sans-serif" fontWeight="900" fontSize="6" fill="#FFFFFF" letterSpacing="0.6">EXPRESS</text>
  </svg>
);
const RUPAY = (
  <svg viewBox="0 0 64 22" aria-label="RuPay">
    <text x="2" y="17" fontFamily="Helvetica, Arial, sans-serif" fontWeight="900" fontSize="16" fill="#097E37">Ru</text>
    <text x="24" y="17" fontFamily="Helvetica, Arial, sans-serif" fontWeight="900" fontSize="16" fill="#E97826">Pay</text>
  </svg>
);
const PAYPAL = (
  <svg viewBox="0 0 64 22" aria-label="PayPal">
    <text x="2" y="17" fontFamily="Helvetica, Arial, sans-serif" fontWeight="900" fontStyle="italic" fontSize="14" fill="#003087">Pay</text>
    <text x="30" y="17" fontFamily="Helvetica, Arial, sans-serif" fontWeight="900" fontStyle="italic" fontSize="14" fill="#009CDE">Pal</text>
  </svg>
);
const UPI = (
  <svg viewBox="0 0 64 22" aria-label="UPI">
    <text x="2" y="17" fontFamily="Helvetica, Arial, sans-serif" fontWeight="900" fontSize="15" fill="#097E37">UPI</text>
    <circle cx="36" cy="11" r="4" fill="none" stroke="#E97826" strokeWidth="2"/>
  </svg>
);

const BADGES = [
  { id: 'visa', label: 'Verified by Visa', node: VISA, trustLine: 'Verified by Visa' },
  { id: 'mc', label: 'Mastercard SecureCode', node: MASTERCARD, trustLine: 'Mastercard SecureCode' },
  { id: 'amex', label: 'Amex SafeKey', node: AMEX, trustLine: 'Amex SafeKey' },
  { id: 'rupay', label: 'RuPay', node: RUPAY, trustLine: 'RuPay accepted' },
  { id: 'paypal', label: 'PayPal', node: PAYPAL, trustLine: 'PayPal protected' },
  { id: 'upi', label: 'UPI', node: UPI, trustLine: 'UPI ready' },
];

export default function PaymentBadges({ size = 'sm', showTrustLine = false }) {
  const cls = size === 'md'
    ? 'w-16 h-10 px-2 bg-white rounded-md border border-border shadow-sm flex items-center justify-center'
    : 'w-12 h-8 px-1.5 bg-white rounded border border-border flex items-center justify-center';

  return (
    <div data-testid="payment-badges" className="inline-flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        {BADGES.map(b => (
          <div key={b.id} data-testid={`payment-badge-${b.id}`} title={b.trustLine} className={cls} aria-label={b.label}>
            {b.node}
          </div>
        ))}
      </div>
      {showTrustLine && (
        <div className="text-[11px] text-ink/60 leading-snug">
          We accept all major Indian and international credit / debit cards through PCI-DSS certified gateways.
          Card data is never stored on BharatCart servers.
        </div>
      )}
    </div>
  );
}
